/**
 * UnsavedChangesContext — tracks dirty state across the app and shows
 * a confirmation dialog before discarding unsaved content.
 *
 * Usage:
 *   const { setHasUnsavedChanges, confirmNavigation } = useUnsavedChangesContext();
 *
 *   // Mark form as dirty / clean:
 *   setHasUnsavedChanges(isDirty);
 *
 *   // Gate a programmatic navigation (prev/next day, back button, etc.):
 *   confirmNavigation(
 *     () => navigate('/somewhere'),   // called on "Discard"
 *     () => {},                       // called on "Keep editing" (optional)
 *   );
 *
 * React-Router navigation is automatically intercepted by <NavigationBlocker />
 * which must be rendered inside the Router (e.g. inside AppShell).
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import UnsavedChangesDialog from '@/components/common/UnsavedChangesDialog';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface UnsavedChangesContextValue {
  hasUnsavedChanges:    boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  /**
   * If dirty: opens the dialog; on "Discard" clears dirty flag and calls onConfirm.
   * If clean: calls onConfirm immediately.
   */
  confirmNavigation: (onConfirm: () => void, onCancel?: () => void) => void;
}

// ─── Context ───────────────────────────────────────────────────────────────────

const UnsavedChangesContext = createContext<UnsavedChangesContextValue>({
  hasUnsavedChanges:    false,
  setHasUnsavedChanges: () => {},
  confirmNavigation:    (fn) => fn(),
});

// ─── Provider ──────────────────────────────────────────────────────────────────

export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [dialogOpen, setDialogOpen]               = useState(false);

  // Store pending callbacks in refs so they're not stale inside closures
  const pendingConfirm = useRef<(() => void) | null>(null);
  const pendingCancel  = useRef<(() => void) | null>(null);

  const confirmNavigation = useCallback(
    (onConfirm: () => void, onCancel?: () => void) => {
      if (!hasUnsavedChanges) {
        onConfirm();
        return;
      }
      pendingConfirm.current = onConfirm;
      pendingCancel.current  = onCancel ?? null;
      setDialogOpen(true);
    },
    [hasUnsavedChanges],
  );

  const handleDiscard = useCallback(() => {
    setDialogOpen(false);
    setHasUnsavedChanges(false);
    pendingConfirm.current?.();
    pendingConfirm.current = null;
    pendingCancel.current  = null;
  }, []);

  const handleStay = useCallback(() => {
    setDialogOpen(false);
    pendingCancel.current?.();
    pendingConfirm.current = null;
    pendingCancel.current  = null;
  }, []);

  return (
    <UnsavedChangesContext.Provider
      value={{ hasUnsavedChanges, setHasUnsavedChanges, confirmNavigation }}
    >
      {children}

      {/* Dialog rendered at provider level so it floats above everything */}
      <UnsavedChangesDialog
        open={dialogOpen}
        onDiscard={handleDiscard}
        onStay={handleStay}
      />
    </UnsavedChangesContext.Provider>
  );
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useUnsavedChangesContext(): UnsavedChangesContextValue {
  return useContext(UnsavedChangesContext);
}
